import { inject, Injectable, isDevMode, OnDestroy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { IDatasource, IGetRowsParams, SortModelItem } from 'ag-grid-community';
import { BehaviorSubject, distinctUntilChanged, filter, interval, map, Observable, share, Subject } from 'rxjs';
import { DATA_GRID_DATA_SOURCES, DATA_GRID_FILTER_SERVICE, DATA_GRID_READER_SERVICE } from './injection-tokens';
import {
  DataSourceRequestStatus,
  GridDataSources,
  GridDataSourceSortFilter,
  GridDataSourcesReadConfig,
  GridFilter,
  GridFilterService,
  GridSort,
  ReaderObject,
  ReaderService,
} from './types';

@Injectable()
export class GridDataSourceInfiniteService<DM> implements IDatasource, OnDestroy {
  private readonly dataSource =
    inject<GridDataSources<DM, GridDataSourcesReadConfig<GridSort[], GridFilter>>>(DATA_GRID_DATA_SOURCES);
  private readonly filterService = inject<GridFilterService<DM>>(DATA_GRID_FILTER_SERVICE);
  private readonly readerService = inject<ReaderService<DM>>(DATA_GRID_READER_SERVICE);

  private readonly requestStatusSubject$ = new Subject<DataSourceRequestStatus>();
  private readonly totalSubject$ = new BehaviorSubject<number | null>(null);
  private readonly sort: GridDataSourceSortFilter<GridSort> = {
    hasChange: false,
    value: [],
    originalValue: [],
  };

  readonly dataChange$: Observable<ReaderObject<DM>[]> = interval(3000).pipe(
    filter(() => this.readerService.shouldRead()),
    map(() => this.dataSource.readByIds(this.readerService.ids())),
    map((rows) => this.readerService.filterChanged(rows() as ReaderObject<DM>[])),
    filter((ar) => ar.length > 0),
    share(),
  );

  // readonly dataChange$: Signal<ReaderObject<DM>[]> = computed(() => {
  //   if (this.readerService.shouldRead() && this.dataSource.readDataListener()) {
  //     const rows = this.dataSource.readByIds(this.readerService.ids());
  //     const data = this.readerService.filterChanged(rows() as ReaderObject<DM>[]);
  //     if (data.length > 0) return data;
  //   }
  //   return [];
  // });
  // (this.dataSource.readDataListener() as Observable<any>).pipe(
  //   filter(() => this.readerService.shouldRead()),
  //   switchMap(() => this.dataSource.readByIds(this.readerService.ids())),
  //   map((rows) => this.readerService.filterChanged(rows as ReaderObject<DM>[])),
  //   filter((ar) => ar.length > 0),
  //   share()
  // );

  readonly requestStatus$ = this.requestStatusSubject$.asObservable();
  readonly requestStatusSignal = toSignal(this.requestStatusSubject$.asObservable());
  // readonly requestStatusComp = ()=> computed(()=>this.));
  readonly getRowFromSing = (params: IGetRowsParams) => {
    this.mapSort(params.sortModel);
    this.dataSource.read({
      startRow: params.startRow,
      endRow: params.endRow,
      sortModel: this.sort.value as GridSort[],
      filterModel: this.filterService.getFilter() as GridFilter,
    });
  };
  readonly total$ = this.totalSubject$.asObservable().pipe(distinctUntilChanged());

  rowCount?: number | undefined;

  getRows({ startRow, endRow, sortModel, successCallback, failCallback }: IGetRowsParams): void {
    this.mapSort(sortModel);

    const gridFilter = this.filterService.getFilter();
    const readParams: GridDataSourcesReadConfig<GridSort[], GridFilter> = {
      startRow,
      endRow,
      sortModel: this.sort.value,
      filterModel: gridFilter as GridFilter,
    };

    this.requestStatusSubject$.next('before');
    const readReturn = this.dataSource.read(readParams);
    // if (readReturn instanceof Observable) {
    //   readReturn.subscribe({
    //     next: ({ rows, total }) => {
    //       successCallback(rows, total);
    //       this.totalSubject$.next(total);
    //       this.requestStatusSubject$.next('success');
    //       this.readerService.newDataLoaded(rows as ReaderObject<DM>[]);
    //     },
    //     error: (err) => {
    //       if (isDevMode()) {
    //         console.error('Grid DataSourceService ', err);
    //       }
    //       failCallback();
    //       this.requestStatusSubject$.next('error');
    //     },
    //   });
    // }
    // if (isSignal(readReturn)) {
    try {
      const { rows, total } = readReturn();
      successCallback(rows, total);
      this.totalSubject$.next(total);
      this.requestStatusSubject$.next('success');
      this.readerService.newDataLoaded(rows as ReaderObject<DM>[]);
    } catch (err) {
      if (isDevMode()) {
        console.error('Grid DataSourceService ', err);
      }
      failCallback();
      this.requestStatusSubject$.next('error');
    }
    // }
  }

  destroy?(): void {
    this.resetReader();
  }

  ngOnDestroy(): void {
    this.requestStatusSubject$.complete();
    this.totalSubject$.complete();
  }

  resetReader(): void {
    this.readerService.resetReader();
  }

  addReader(rows: ReaderObject<DM>[]): void {
    this.readerService.newDataLoaded(rows);
  }

  private mapSort(sortModel: SortModelItem[]) {
    const gridSport: GridSort[] = sortModel.map(({ colId, sort }) => ({ field: colId, sort }));
    this.sort.hasChange = this.sort.originalValue !== gridSport;

    if (this.sort.hasChange) {
      this.sort.originalValue = gridSport;
      this.sort.value = gridSport;
    }
  }
  // private getRowNext = ({ rows, total }) => {
  //   successCallback(rows, total);
  //   this.totalSubject$.next(total);
  //   this.requestStatusSubject$.next('success');
  //   this.readerService.newDataLoaded(rows as ReaderObject<DM>[]);
  // },
  // private getRowError = ()=>{}
}
